/* eslint-disable react/jsx-props-no-spreading */
import { useAppDispatch, useAppSelector } from 'app/config/storeConfig/hooks';
import { useGetWarehouseBoxesQuery } from 'entities/Box';
import { Floor } from 'entities/Floor';
import { getSizeCodeTypeString, useGetSizeCodesByWarehouseIdQuery } from 'entities/SizeCode';
import Konva from 'konva';
import { KonvaEventObject } from 'konva/lib/Node';
import React, { FC, memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Layer, Rect, Stage, Transformer } from 'react-konva';
import { roundNumber } from 'shared/utils/helpers/roundNumber';
import { useAppTranslation } from 'app/config/i18Config/hooks';
import { deserializeMapConfig } from '../model/mapConfigSerialization';
import { getImageTransform } from '../model/selectors/getImageTransform';
import { getMapConfig } from '../model/selectors/getMapConfig';
import { getSnapStepPixels } from '../model/selectors/getSnapStepPixels';
import { getShapes } from '../model/selectors/getShapes';
import { getVertexEditShapeId } from '../model/selectors/getVertexEditShapeId';
import { deserializeShapes } from '../model/serialization';
import { warehouseMapConstructorActions } from '../model/slice/warehouseMapConstructorSlice';
import { ShapeType, WarehouseMapTool } from '../model/types';
import { Point, screenToWorld, snapToGrid } from '../utils/helpers/coordinates';
import { getShapesBounds } from '../utils/helpers/getShapesBounds';
import { getPolygonBounds } from '../utils/helpers/shapeGeometry';
import { sortShapesByZIndex } from '../utils/helpers/shapeZIndex';
import { useCanvasDimensions } from '../utils/hooks/useCanvasDimensions';
import { useCanvasGrid } from '../utils/hooks/useCanvasGrid';
import { computeFitView, useCanvasScale } from '../utils/hooks/useCanvasScale';
import { useBackgroundScaleMeasure } from '../utils/hooks/useBackgroundScaleMeasure';
import { useClipboard } from '../utils/hooks/useClipboard';
import { useFloorSchemaImage } from '../utils/hooks/useFloorSchemaImage';
import { useImageTransform } from '../utils/hooks/useImageTransform';
import { useKeyboardShortcuts } from '../utils/hooks/useKeyboardShortcuts';
import { useMouseArea } from '../utils/hooks/useMouseArea';
import { usePolygonDrawing } from '../utils/hooks/usePolygonDrawing';
import { useTool } from '../utils/hooks/useTool';
import { useTransform } from '../utils/hooks/useTransform';
import { useWatchFloorConfigChanges } from '../utils/hooks/useWatchFloorConfigChanges';
import { BackgroundScaleMeasurePreview } from './BackgroundScaleMeasurePreview';
import { CanvasContextMenu, ContextMenuItem } from './CanvasContextMenu';
import { ConstructorBackground } from './ConstructorBackground';
import { FloorSwitch } from './FloorSwitch';
import { OriginCrosshair } from './OriginCrosshair';
import { ParametersBar } from './ParametersBar';
import { PolygonPreview } from './PolygonPreview';
import { PolygonVertexEditor } from './PolygonVertexEditor';
import { ToolBar } from './ToolBar';
import { WarehouseMapShapes } from './WarehouseMapShapes';

interface WarehouseMapConstructorProps {
  floors: Floor[] | undefined;
  selectedFloor: Floor;
  warehouseId: string | undefined;
  shapesConfig?: Nullable<string>;
  refetchFloors: () => void;
}

export const WarehouseMapConstructor: FC<WarehouseMapConstructorProps> = memo((props) => {
  const { floors, selectedFloor, warehouseId, shapesConfig, refetchFloors } = props;

  const { t } = useAppTranslation('warehouse-management');
  const dispatch = useAppDispatch();

  const shapes = useAppSelector(getShapes);
  const snapStepPixels = useAppSelector(getSnapStepPixels);
  const imageTransform = useAppSelector(getImageTransform);
  const mapConfig = useAppSelector(getMapConfig);

  const { stageParentRef, canvasDimensions } = useCanvasDimensions();
  const { activeTool, onChangeTool } = useTool();
  const { canvasPosition, canvasScale, onMouseWheel, onStageDragMove, onStageDragEnd, setView } = useCanvasScale();
  const { transformerRef, stageRef, layerRef, resizeByGrid } = useTransform({ shapes });
  const { gridRef, onDragMove } = useCanvasGrid(canvasDimensions, canvasScale, stageRef, canvasPosition);
  const backgroundRef = useRef<Konva.Image>(null);
  const { backgroundTransformerRef } = useImageTransform({ activeTool, imageTransform, backgroundRef });
  const backgroundScaleMeasure = useBackgroundScaleMeasure(activeTool);

  const clipboard = useClipboard();

  const [labelEditShapeId, setLabelEditShapeId] = useState<Nullable<string>>(null);

  const vertexEditShapeId = useAppSelector(getVertexEditShapeId);
  const vertexEditShapeIdRef = useRef(vertexEditShapeId);
  vertexEditShapeIdRef.current = vertexEditShapeId;

  const { image, loadImageSrc } = useFloorSchemaImage();

  const filteredShapesByFloor = useMemo(
    () => shapes.filter((shape) => shape.floorId === selectedFloor.floorId),
    [selectedFloor.floorId, shapes],
  );

  const selectedShapes = useMemo(() => filteredShapesByFloor.filter((s) => s.selected), [filteredShapesByFloor]);
  const selectedShape = selectedShapes[0];
  const renderedShapes = useMemo(() => sortShapesByZIndex(filteredShapesByFloor), [filteredShapesByFloor]);

  const floorSchemaImage = floors?.find((floor) => floor.floorId === selectedFloor.floorId)?.floorSchemaBackgroundUrl;

  const { data } = useGetSizeCodesByWarehouseIdQuery(warehouseId, { skip: !warehouseId });
  const { data: boxes } = useGetWarehouseBoxesQuery({ warehouseId }, { skip: !warehouseId });
  const sizeCodeLabelsById = useMemo(
    () =>
      Object.fromEntries(
        (data ?? []).map((sizeCode) => [sizeCode.sizeCodeId, `${sizeCode.square} ${getSizeCodeTypeString(sizeCode.sizeCodeType)}`]),
      ),
    [data],
  );
  const sizeCodeByBoxId = useMemo(
    () => Object.fromEntries((boxes ?? []).filter((box) => Boolean(box.sizeCodeId)).map((box) => [box.boxId, box.sizeCodeId])),
    [boxes],
  );

  const polygonDrawing = usePolygonDrawing({
    activeTool,
    floorId: selectedFloor.floorId,
    availableSizeCodes: data,
    canvasScale,
  });

  useEffect(() => {
    loadImageSrc(floorSchemaImage);
  }, [floorSchemaImage, loadImageSrc]);

  const [shapesHydrated, setShapesHydrated] = useState(false);

  useEffect(() => {
    const parsedShapes = deserializeShapes(shapesConfig);
    dispatch(warehouseMapConstructorActions.setInitialShapes(parsedShapes));
    setShapesHydrated(true);
  }, [dispatch, shapesConfig]);

  useEffect(() => {
    if (!shapesHydrated || Object.keys(sizeCodeByBoxId).length === 0) return;
    dispatch(warehouseMapConstructorActions.syncShapeSizeCodes(sizeCodeByBoxId));
  }, [dispatch, selectedFloor.floorId, shapesHydrated, sizeCodeByBoxId]);

  useEffect(() => {
    dispatch(warehouseMapConstructorActions.setMapConfig(deserializeMapConfig(selectedFloor.mapConfig)));
  }, [dispatch, selectedFloor.floorId, selectedFloor.mapConfig]);

  const fitToCurrentFloor = useCallback((): void => {
    const bounds = getShapesBounds(filteredShapesByFloor);
    setView(computeFitView(bounds, canvasDimensions));
  }, [filteredShapesByFloor, canvasDimensions, setView]);

  const fittedFloorIdsRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    const floorId = selectedFloor.floorId;
    if (fittedFloorIdsRef.current.has(floorId)) return;
    if (!shapesHydrated) return;
    if (!canvasDimensions.width || !canvasDimensions.height) return;

    fittedFloorIdsRef.current.add(floorId);
    const bounds = getShapesBounds(filteredShapesByFloor);
    setView(computeFitView(bounds, canvasDimensions));
  }, [selectedFloor.floorId, filteredShapesByFloor, canvasDimensions, setView, shapesHydrated]);

  const unselectShapes = useCallback((): void => {
    dispatch(warehouseMapConstructorActions.unselectShapes());
  }, [dispatch]);

  const {
    onMouseDown: mouseAreaOnMouseDown,
    onMouseUp,
    onMouseMove: mouseAreaOnMouseMove,
    onMouseLeave,
    previewLayerRef,
    selectedArea,
    lastCanvasCursorRef,
    cancelPreview,
    isPreviewingNonPolygon,
  } = useMouseArea({
    activeTool,
    transformerRef,
    floorId: selectedFloor.floorId,
    availableSizeCodes: data,
  });

  // Composite mouse-move: polygon drawing updates rubber-band first, then
  // useMouseArea updates lastCanvasCursorRef and handles non-polygon previews.
  const onMouseMove = useCallback(
    (e: KonvaEventObject<MouseEvent>): void => {
      if (backgroundScaleMeasure.onMouseMove(e)) return;
      polygonDrawing.onMouseMove(e);
      mouseAreaOnMouseMove(e);
    },
    [backgroundScaleMeasure, polygonDrawing, mouseAreaOnMouseMove],
  );

  const getDropPoint = useCallback((): Point => {
    const cursor = lastCanvasCursorRef.current;
    if (cursor) {
      return snapToGrid(cursor, snapStepPixels);
    }
    const { width, height } = canvasDimensions;
    const worldCenter = screenToWorld({ x: width / 2, y: height / 2 }, canvasPosition, canvasScale);
    return snapToGrid(worldCenter, snapStepPixels);
  }, [canvasDimensions, canvasPosition, canvasScale, lastCanvasCursorRef, snapStepPixels]);

  // ── Vertex edit ─────────────────────────────────────────────────────────────
  const vertexEditOriginalPointsRef = useRef<number[]>([]);

  const onVertexEditCommit = useCallback((): void => {
    dispatch(warehouseMapConstructorActions.endVertexEdit());
  }, [dispatch]);

  const onVertexEditCancel = useCallback((): void => {
    const id = vertexEditShapeIdRef.current;
    const original = vertexEditOriginalPointsRef.current;

    if (id && original.length) {
      dispatch(warehouseMapConstructorActions.updateShapeParams({ id, params: { points: original } }));
    }

    dispatch(warehouseMapConstructorActions.endVertexEdit());
  }, [dispatch]);

  useKeyboardShortcuts({
    floorId: selectedFloor.floorId,
    clipboard,
    polygonHook: polygonDrawing,
    cancelNonPolygonPreview: cancelPreview,
    isPreviewingNonPolygon,
    vertexEditShapeId,
    onVertexEditCommit,
    onVertexEditCancel,
    activeTool,
  });

  const dragSourceShapeIdRef = useRef<Nullable<string>>(null);
  const dragSelectionIdsRef = useRef<string[]>([]);
  const [isMiddleMousePanning, setIsMiddleMousePanning] = useState(false);
  const didStartMiddleDragRef = useRef(false);
  const isMiddleDownRef = useRef(false);
  const isPanMode = activeTool === WarehouseMapTool.GRAB || isMiddleMousePanning;

  const stopMiddleMousePan = useCallback((): void => {
    setIsMiddleMousePanning(false);
    isMiddleDownRef.current = false;
    if (didStartMiddleDragRef.current) {
      stageRef.current?.stopDrag();
      didStartMiddleDragRef.current = false;
    }
  }, [stageRef]);

  useEffect(() => {
    const onWindowMouseUp = (event: MouseEvent): void => {
      if (event.button === 1) {
        stopMiddleMousePan();
      }
    };

    window.addEventListener('mouseup', onWindowMouseUp);

    return () => {
      window.removeEventListener('mouseup', onWindowMouseUp);
    };
  }, [stopMiddleMousePan]);

  const handleStageMouseDown = useCallback(
    (e: KonvaEventObject<MouseEvent>): void => {
      if (e.evt.button === 1) {
        e.evt.preventDefault();
        e.evt.stopPropagation();
        isMiddleDownRef.current = true;
        setIsMiddleMousePanning(true);
        didStartMiddleDragRef.current = true;

        const stage = stageRef.current;
        if (stage && e.target !== stage) {
          e.target.stopDrag();
        }

        requestAnimationFrame(() => {
          stageRef.current?.startDrag();
        });
        return;
      }

      if (backgroundScaleMeasure.onMouseDown(e)) return;

      if (activeTool === WarehouseMapTool.EDIT_BACKGROUND) return;

      // Label position edit mode: click outside the label exits the mode.
      if (labelEditShapeId) {
        const targetName = (e.target as Konva.Shape).name?.() ?? '';
        if (targetName !== 'label-handle') setLabelEditShapeId(null);
        return;
      }

      // Vertex edit mode: clicks outside vertex/midpoint handles commit the edit.
      if (vertexEditShapeIdRef.current) {
        const targetName = (e.target as Konva.Shape).name?.() ?? '';

        if (targetName !== 'vertex-handle' && targetName !== 'midpoint-handle') {
          dispatch(warehouseMapConstructorActions.endVertexEdit());
        }

        return;
      }

      // Polygon drawing consumes the click; skip useMouseArea when true.
      if (polygonDrawing.onMouseDown(e)) return;

      mouseAreaOnMouseDown(e);
    },
    [activeTool, backgroundScaleMeasure, dispatch, labelEditShapeId, mouseAreaOnMouseDown, polygonDrawing, stageRef],
  );

  const handleStageMouseUp = useCallback(
    (e: KonvaEventObject<MouseEvent>): void => {
      if (e.evt.button === 1) {
        stopMiddleMousePan();
      }

      onMouseUp(e);
    },
    [onMouseUp, stopMiddleMousePan],
  );

  const handleStageMouseLeave = useCallback((): void => {
    stopMiddleMousePan();
    onMouseLeave();
  }, [onMouseLeave, stopMiddleMousePan]);

  const handleStageDblClick = useCallback(
    (e: KonvaEventObject<MouseEvent>): void => {
      // Polygon tool: double-click closes the polygon being drawn.
      polygonDrawing.onDblClick(e);
    },
    [polygonDrawing],
  );

  const startVertexEdit = useCallback(
    (shapeId: string): void => {
      const shape = shapes.find((s) => s.id === shapeId);
      if (!shape || (shape.type !== ShapeType.POLYGON && shape.type !== ShapeType.POLYGON_BOX)) return;
      vertexEditOriginalPointsRef.current = 'points' in shape ? [...shape.points] : [];
      dispatch(warehouseMapConstructorActions.startVertexEdit(shapeId));
    },
    [dispatch, shapes],
  );

  const handleShapeDragStart = useCallback(
    (e: KonvaEventObject<MouseEvent>): void => {
      if (isMiddleDownRef.current) {
        e.target.stopDrag();
        return;
      }

      const shapeId = e.target.attrs?.id as string | undefined;

      dragSourceShapeIdRef.current = shapeId || null;
      dragSelectionIdsRef.current =
        transformerRef.current
          ?.nodes()
          .map((node) => node.attrs?.id)
          .filter((id): id is string => Boolean(id)) || [];

      if (!shapeId || !dragSelectionIdsRef.current.includes(shapeId)) {
        dragSelectionIdsRef.current = shapeId ? [shapeId] : [];
      }
    },
    [transformerRef],
  );

  const updateShapeCoords = useCallback(
    (e: KonvaEventObject<MouseEvent>): void => {
      if (!('id' in e.target.attrs)) return;

      const shapeId = e.target.attrs?.id as string;
      const dragSourceId = dragSourceShapeIdRef.current;
      const isDragSourceEvent = dragSourceId === shapeId;

      if (dragSourceId && dragSourceId !== shapeId) {
        return;
      }

      const sourceShape = shapes.find((shape) => shape.id === shapeId);

      if (!sourceShape) {
        if (dragSourceId === shapeId) {
          dragSourceShapeIdRef.current = null;
          dragSelectionIdsRef.current = [];
        }
        return;
      }

      // POLYGON renders as a plain Line node (points live in e.target.attrs).
      // POLYGON_BOX renders as a Group (points are on the inner Line child, not in
      // the Group's attrs), so we must pull the old points from the Redux shape and
      // apply the Group's drag offset to them.
      const hasPointsInAttrs = 'points' in e.target.attrs;
      const hasPointsInShape = 'points' in sourceShape;

      const dragOffsetX = e.target.x();
      const dragOffsetY = e.target.y();

      const newPoints = hasPointsInAttrs
        ? (e.target.attrs.points as number[]).map((p, i) => (i % 2 === 0 ? p + dragOffsetX : p + dragOffsetY))
        : hasPointsInShape
        ? (sourceShape as { points: number[] }).points.map((p, i) => (i % 2 === 0 ? p + dragOffsetX : p + dragOffsetY))
        : undefined;

      const attrs = newPoints
        ? { x: 0, y: 0, points: newPoints }
        : {
            x: Math.round(dragOffsetX / snapStepPixels) * snapStepPixels,
            y: Math.round(dragOffsetY / snapStepPixels) * snapStepPixels,
          };

      const deltaX = newPoints ? dragOffsetX : (attrs as { x: number }).x - sourceShape.x;
      const deltaY = newPoints ? dragOffsetY : (attrs as { y: number }).y - sourceShape.y;

      e.target.setAttrs(attrs);

      dispatch(
        warehouseMapConstructorActions.updateShapeParams({
          id: shapeId,
          params: attrs,
        }),
      );

      const draggedSelectionIds = dragSelectionIdsRef.current;
      const isMultiSelected = isDragSourceEvent && draggedSelectionIds.length > 1;

      if (isMultiSelected && (deltaX !== 0 || deltaY !== 0)) {
        dispatch(
          warehouseMapConstructorActions.moveShapesByIds({
            shapeIds: draggedSelectionIds,
            deltaX,
            deltaY,
            excludedShapeId: shapeId,
          }),
        );
      }

      if (isDragSourceEvent) {
        dragSourceShapeIdRef.current = null;
        dragSelectionIdsRef.current = [];
      }
    },
    [dispatch, snapStepPixels, shapes],
  );

  const updateShapeParams = useCallback(
    (e: KonvaEventObject<MouseEvent>): void => {
      if (!('id' in e.target.attrs)) return;

      const shapeId = e.target.attrs?.id as string;
      const shape = shapes.find((item) => item.id === shapeId);
      if (!shape) return;

      const roundedX = shape.type === ShapeType.POLYGON ? 0 : roundNumber(e.target.x(), 0);
      const roundedY = shape.type === ShapeType.POLYGON ? 0 : roundNumber(e.target.y(), 0);
      const rotation = e.target.rotation();
      const scaleX = e.target.scaleX();
      const scaleY = e.target.scaleY();

      if (shape.type === ShapeType.RECTANGLE) {
        const nextWidth = Math.max(1, roundNumber(e.target.width() * scaleX, 0));
        const nextHeight = Math.max(1, roundNumber(e.target.height() * scaleY, 0));

        e.target.setAttrs({
          x: roundedX,
          y: roundedY,
          width: nextWidth,
          height: nextHeight,
          scaleX: 1,
          scaleY: 1,
          rotation,
        });

        dispatch(
          warehouseMapConstructorActions.updateShapeParams({
            id: shapeId,
            params: {
              x: roundedX,
              y: roundedY,
              width: nextWidth,
              height: nextHeight,
              scaleX: 1,
              scaleY: 1,
              rotation,
            },
          }),
        );
        return;
      }

      if (shape.type === ShapeType.CIRCLE) {
        const nextRadiusX = Math.max(1, roundNumber(shape.radiusX * scaleX, 0));
        const nextRadiusY = Math.max(1, roundNumber(shape.radiusY * scaleY, 0));

        e.target.setAttrs({
          x: roundedX,
          y: roundedY,
          scaleX: 1,
          scaleY: 1,
          rotation,
        });

        dispatch(
          warehouseMapConstructorActions.updateShapeParams({
            id: shapeId,
            params: {
              x: roundedX,
              y: roundedY,
              radiusX: nextRadiusX,
              radiusY: nextRadiusY,
              scaleX: 1,
              scaleY: 1,
              rotation,
            },
          }),
        );
        return;
      }

      e.target.setAttrs({
        x: roundedX,
        y: roundedY,
        scaleX: 1,
        scaleY: 1,
        rotation,
      });

      dispatch(
        warehouseMapConstructorActions.updateShapeParams({
          id: shapeId,
          params: {
            x: roundedX,
            y: roundedY,
            scaleX: 1,
            scaleY: 1,
            rotation,
          },
        }),
      );
    },
    [dispatch, shapes],
  );

  const prevActiveToolRef = useRef<WarehouseMapTool | undefined>(undefined);
  useEffect(() => {
    if (prevActiveToolRef.current !== undefined && prevActiveToolRef.current !== activeTool) {
      unselectShapes();
    }
    prevActiveToolRef.current = activeTool;
  }, [activeTool, unselectShapes]);

  const floorConfigChanges = useWatchFloorConfigChanges(shapesConfig, selectedFloor.mapConfig);
  const { shapesChanges, isMapConfigChanged } = floorConfigChanges;
  const isShapesChanged = Object.values(shapesChanges).some((value) => Boolean(value.length));
  const isResizableShape = selectedShape?.type === ShapeType.RECTANGLE || selectedShape?.type === ShapeType.CIRCLE;

  const vertexEditShape = useMemo(() => {
    if (!vertexEditShapeId) return null;
    const s = shapes.find((sh) => sh.id === vertexEditShapeId);

    if (s && (s.type === ShapeType.POLYGON || s.type === ShapeType.POLYGON_BOX)) return s;

    return null;
  }, [shapes, vertexEditShapeId]);

  const lockedSelectionBounds = useMemo(() => {
    if (!selectedShape?.locked) return null;
    const rotation = selectedShape.rotation ?? 0;
    if (selectedShape.type === ShapeType.RECTANGLE) {
      return { x: selectedShape.x, y: selectedShape.y, width: selectedShape.width, height: selectedShape.height, rotation };
    }
    if (selectedShape.type === ShapeType.CIRCLE) {
      return {
        x: selectedShape.x - selectedShape.radiusX,
        y: selectedShape.y - selectedShape.radiusY,
        width: selectedShape.radiusX * 2,
        height: selectedShape.radiusY * 2,
        rotation,
      };
    }
    if ('points' in selectedShape) {
      const bounds = getPolygonBounds(selectedShape.points);
      return { x: bounds.minX, y: bounds.minY, width: bounds.width, height: bounds.height, rotation };
    }
    return null;
  }, [selectedShape]);

  // ── Context menu ────────────────────────────────────────────────────────────
  const [contextMenu, setContextMenu] = useState<{ x: number; y: number; onShape: boolean } | null>(null);

  const handleContextMenu = useCallback(
    (e: KonvaEventObject<MouseEvent>): void => {
      e.evt.preventDefault();

      // No context menu while polygon is being drawn.
      if (polygonDrawing.isDrawing) return;

      const shapeId: string | undefined = (e.target.attrs as Record<string, unknown>).id as string | undefined;
      const ancestorId: string | undefined = (e.target.findAncestor('.shape') as { attrs?: { id?: string } } | null)?.attrs?.id;
      const clickedId = shapeId || ancestorId;

      if (clickedId) {
        const alreadySelected = shapes.some((s) => s.id === clickedId && s.selected);
        if (!alreadySelected) {
          dispatch(warehouseMapConstructorActions.selectShape(clickedId));
        }
      }

      setContextMenu({
        x: e.evt.clientX,
        y: e.evt.clientY,
        onShape: Boolean(clickedId),
      });
    },
    [dispatch, polygonDrawing.isDrawing, shapes],
  );

  const closeContextMenu = useCallback((): void => {
    setContextMenu(null);
  }, []);

  const handleLabelDragEnd = useCallback(
    (shapeId: string, offsetX: number, offsetY: number): void => {
      dispatch(warehouseMapConstructorActions.updateShapeParams({ id: shapeId, params: { labelOffsetX: offsetX, labelOffsetY: offsetY } }));
      setLabelEditShapeId(null);
    },
    [dispatch],
  );

  useEffect(() => {
    if (!labelEditShapeId) return;
    const onKeyDown = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') setLabelEditShapeId(null);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [labelEditShapeId]);

  const contextMenuItems = useMemo((): ContextMenuItem[] => {
    if (!contextMenu) return [];

    const selectionCount = shapes.filter((s) => s.selected).length;
    const hasSelection = selectionCount > 0;
    const isSingleSelect = selectionCount === 1;
    const singleSelectedShape = isSingleSelect ? shapes.find((s) => s.selected) : undefined;

    if (!contextMenu.onShape) {
      return [
        {
          key: 'paste',
          label: t('Paste'),
          shortcut: 'Ctrl+V',
          disabled: !clipboard.hasClipboard(),
          onClick: () => {
            clipboard.paste(selectedFloor.floorId);
          },
        },
        { key: 'divider-1', label: '', isDivider: true },
        {
          key: 'select-all',
          label: t('Select all'),
          shortcut: 'Ctrl+A',
          onClick: () => {
            dispatch(warehouseMapConstructorActions.selectAllShapes({ floorId: selectedFloor.floorId }));
          },
        },
      ];
    }

    return [
      {
        key: 'duplicate',
        label: t('Duplicate'),
        shortcut: 'Ctrl+D',
        disabled: !hasSelection,
        onClick: () => {
          dispatch(warehouseMapConstructorActions.duplicateSelectedShapes({ offsetX: snapStepPixels, offsetY: snapStepPixels }));
        },
      },
      { key: 'divider-1', label: '', isDivider: true },
      {
        key: 'copy',
        label: t('Copy'),
        shortcut: 'Ctrl+C',
        disabled: !hasSelection,
        onClick: () => {
          clipboard.copy();
        },
      },
      {
        key: 'cut',
        label: t('Cut'),
        shortcut: 'Ctrl+X',
        disabled: !hasSelection,
        onClick: () => {
          clipboard.cut();
        },
      },
      {
        key: 'paste',
        label: t('Paste'),
        shortcut: 'Ctrl+V',
        disabled: !clipboard.hasClipboard(),
        onClick: () => {
          clipboard.paste(selectedFloor.floorId);
        },
      },
      { key: 'divider-2', label: '', isDivider: true },
      {
        key: 'lock',
        label: singleSelectedShape?.locked ? t('Unlock') : t('Lock'),
        disabled: !isSingleSelect,
        onClick: () => {
          if (singleSelectedShape) dispatch(warehouseMapConstructorActions.toggleLockShape(singleSelectedShape.id));
        },
      },
      ...(isSingleSelect &&
      singleSelectedShape &&
      (singleSelectedShape.type === ShapeType.RECTANGLE || singleSelectedShape.type === ShapeType.CIRCLE)
        ? [
            { key: 'divider-transform', label: '', isDivider: true },
            ...(singleSelectedShape.type === ShapeType.RECTANGLE
              ? [
                  {
                    key: 'swap-dimensions',
                    label: t('Swap length and width'),
                    disabled: singleSelectedShape.locked,
                    onClick: () => {
                      dispatch(warehouseMapConstructorActions.swapRectangleDimensions(singleSelectedShape.id));
                    },
                  },
                ]
              : []),
            {
              key: 'transform-to-polygon',
              label: t('Transform to polygon'),
              disabled: singleSelectedShape.locked,
              onClick: () => {
                dispatch(warehouseMapConstructorActions.transformToPolygon(singleSelectedShape.id));
                // Enter vertex edit immediately; no original points to restore (shape is now a new polygon).
                vertexEditOriginalPointsRef.current = [];
                dispatch(warehouseMapConstructorActions.startVertexEdit(singleSelectedShape.id));
              },
            },
          ]
        : []),
      ...(isSingleSelect &&
      singleSelectedShape &&
      (singleSelectedShape.type === ShapeType.POLYGON || singleSelectedShape.type === ShapeType.POLYGON_BOX)
        ? [
            { key: 'divider-vertices', label: '', isDivider: true },
            {
              key: 'edit-vertices',
              label: t('Edit vertices'),
              disabled: singleSelectedShape.locked,
              onClick: () => {
                startVertexEdit(singleSelectedShape.id);
              },
            },
          ]
        : []),
      ...(isSingleSelect &&
      singleSelectedShape &&
      (singleSelectedShape.type === ShapeType.RECTANGLE || singleSelectedShape.type === ShapeType.POLYGON_BOX)
        ? [
            { key: 'divider-label', label: '', isDivider: true },
            {
              key: 'change-label-position',
              label: t('Change label position'),
              disabled: singleSelectedShape.locked,
              onClick: () => {
                setLabelEditShapeId(singleSelectedShape.id);
              },
            },
          ]
        : []),
      { key: 'divider-3', label: '', isDivider: true },
      {
        key: 'delete',
        label: t('Delete'),
        shortcut: 'Del',
        disabled: !hasSelection,
        danger: true,
        onClick: () => {
          dispatch(warehouseMapConstructorActions.deleteSelected());
        },
      },
    ];
  }, [contextMenu, shapes, clipboard, dispatch, snapStepPixels, selectedFloor.floorId, t, startVertexEdit]);

  return (
    <div
      className="w-full h-[calc(100vh-200px)] bg-white rounded-lg relative"
      ref={stageParentRef}
      onContextMenu={(e) => {
        e.preventDefault();
      }}
    >
      <FloorSwitch floors={floors} isShapesChanged={isShapesChanged || isMapConfigChanged} warehouseId={warehouseId} />
      <ToolBar
        activeTool={activeTool}
        onChange={onChangeTool}
        hasAvailableSizeCodes={Boolean(data?.length)}
        onFitToContent={fitToCurrentFloor}
      />
      <ParametersBar
        selectedShapes={selectedShapes}
        floorId={selectedFloor.floorId}
        warehouseId={warehouseId}
        floorShapes={filteredShapesByFloor}
        refetchFloors={refetchFloors}
        isShapesChanged={isShapesChanged}
        isMapConfigChanged={isMapConfigChanged}
        availableSizeCodes={data}
        getDropPoint={getDropPoint}
        activeTool={activeTool}
        backgroundScaleMeasure={backgroundScaleMeasure}
      />
      <Stage
        ref={stageRef}
        className={`mt-5 ${isPanMode ? 'cursor-grab' : 'cursor-default'}`}
        x={canvasPosition.x}
        y={canvasPosition.y}
        scale={{ x: canvasScale, y: canvasScale }}
        width={canvasDimensions.width}
        height={canvasDimensions.height}
        draggable={isPanMode}
        onWheel={onMouseWheel}
        onDragMove={onStageDragMove}
        onDragEnd={onStageDragEnd}
        onMouseDown={handleStageMouseDown}
        onMouseMove={onMouseMove}
        onMouseUp={handleStageMouseUp}
        onMouseLeave={handleStageMouseLeave}
        onDblClick={handleStageDblClick}
        onContextMenu={handleContextMenu}
      >
        <Layer ref={gridRef} name="grid-layer" listening={false}>
          {/* Grid layer (behind shapes) */}
        </Layer>
        <Layer name="background-layer">
          <ConstructorBackground
            ref={backgroundRef}
            image={image}
            transform={imageTransform}
            activeTool={activeTool}
            onDragEnd={(x, y) => {
              dispatch(warehouseMapConstructorActions.setImageTransform({ x, y }));
            }}
            onTransformEnd={(rotation) => {
              dispatch(warehouseMapConstructorActions.setImageTransform({ rotation }));
            }}
          />
        </Layer>
        <Layer ref={layerRef} listening={activeTool !== WarehouseMapTool.EDIT_BACKGROUND}>
          <WarehouseMapShapes
            shapes={renderedShapes}
            floorLabelFontSize={mapConfig.labelFontSize}
            activeTool={activeTool}
            sizeCodeLabelsById={sizeCodeLabelsById}
            onDragStart={handleShapeDragStart}
            onDragMove={onDragMove}
            onDragEnd={updateShapeCoords}
            onTransformEnd={updateShapeParams}
            labelEditShapeId={labelEditShapeId}
            onLabelDragEnd={handleLabelDragEnd}
          />
          {vertexEditShape && (
            <PolygonVertexEditor
              shape={vertexEditShape}
              canvasScale={canvasScale}
              onChangePoints={(points) => {
                dispatch(warehouseMapConstructorActions.updateShapeParams({ id: vertexEditShape.id, params: { points } }));
              }}
              onCommit={onVertexEditCommit}
              onCancel={onVertexEditCancel}
            />
          )}
        </Layer>
        <Layer>
          {/* Selected area layer */}
          <Rect {...selectedArea} opacity={0.3} fill="blue" stroke="black" strokeWidth={1} listening={false} />
          {!selectedShape?.locked && (
            <Transformer ref={transformerRef} resizeEnabled={isResizableShape} flipEnabled={false} anchorDragBoundFunc={resizeByGrid} />
          )}
          <Transformer ref={backgroundTransformerRef} resizeEnabled={false} flipEnabled={false} />
          {lockedSelectionBounds && (
            <Rect {...lockedSelectionBounds} stroke="#788593" strokeWidth={1} dash={[6, 4]} fill="transparent" listening={false} />
          )}
        </Layer>
        <Layer ref={previewLayerRef} name="preview-layer">
          {polygonDrawing.isDrawing && (
            <PolygonPreview
              vertices={polygonDrawing.vertices}
              rubberBand={polygonDrawing.rubberBand}
              firstVertexHovered={polygonDrawing.firstVertexHovered}
              axisLockGuide={polygonDrawing.axisLockGuide}
              canvasScale={canvasScale}
            />
          )}
          {activeTool === WarehouseMapTool.EDIT_BACKGROUND && <OriginCrosshair canvasScale={canvasScale} />}
          {activeTool === WarehouseMapTool.EDIT_BACKGROUND && (
            <BackgroundScaleMeasurePreview
              start={backgroundScaleMeasure.start}
              end={backgroundScaleMeasure.end}
              canvasScale={canvasScale}
            />
          )}
        </Layer>
      </Stage>

      {/* Context menu — portaled to document.body */}
      {contextMenu && <CanvasContextMenu x={contextMenu.x} y={contextMenu.y} items={contextMenuItems} onClose={closeContextMenu} />}
    </div>
  );
});
